<template>
  <div id="mux-video" :style="{ filter: blur ? 'blur(75px)' : '' }">
    <video
      ref="video"
      preload="auto"
      :loop="loop"
      playsinline
      :muted="isSpeaking"
      @timeupdate="onTimeUpdate"
      @ended="onEnded"
      @play="onPlay"
    >
      <source
        :src="`https://stream.mux.com/${muxId}.m3u8?max_resolution=720p`"
        type="application/x-mpegURL"
      >
    </video>
    <NuxtImg
      v-if="fallbackImage"
      v-show="loaded && !isPlaying"
      :src="fallbackImage"
      :preload="preload"
      :loading="loading"
      :fetch-priority="fetchPriority"
      :alt="alt"
      @load="loaded = true"
    />
  </div>
</template>
<script setup>
import logger from "debug";
import Hls from "hls.js";
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from "vue";

const debug = logger("travvir:debut-nuxt:components:home:MuxVideo");
const props = defineProps({
  muxId: {
    type: String,
    default: "",
  },
  fallbackImage: {
    type: String,
    default: "",
  },
  blur: {
    type: Boolean,
    default: false,
  },
  isSpeaking: {
    type: Boolean,
    default: true,
  },
  pause: {
    type: Boolean,
    default: false,
  },
  loop: {
    type: Boolean,
    default: true,
  },
  fetchPriority: {
    type: String,
    default: "auto", // can be 'high', 'low', or 'auto'
  },
  preload: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: String,
    default: "eager",
  },
  alt: {
    type: String,
    default: "360 virtual tour",
  },
});

const emit = defineEmits(["timeupdate", "ended", "play", "isPlaying"]);

const video = ref(null);
const hls = ref(null);
const observer = ref(null);
const isPlaying = ref(false);
const isInViewport = ref(false);
const loaded = ref(false);
const isSourceReady = ref(false);
const pendingPlay = ref(false);

const markSourceReady = () => {
  isSourceReady.value = true;
  if (pendingPlay.value) {
    pendingPlay.value = false;
    attemptPlay();
  }
};

const initializeVideo = () => {
  const videoEl = video.value;
  if (!videoEl || !props.muxId) return;

  const src = `https://stream.mux.com/${props.muxId}.m3u8?max_resolution=720p`;
  isSourceReady.value = false;

  // Prefer HLS.js (MSE) whenever available. Edge may report canPlayType for
  // HLS but still fail with "no supported source" — MSE avoids that and
  // streams short chunks instead of buffering a large prefix.
  if (Hls.isSupported()) {
    debug("setting up hls.js (MSE)");
    const hlsInstance = new Hls({
      maxBufferLength: 2,
      maxMaxBufferLength: 2,
      enableWorker: true,
      autoStartLoad: true,
    });
    hlsInstance.loadSource(src);
    hlsInstance.attachMedia(videoEl);
    hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
      debug("hls manifest parsed — source ready");
      markSourceReady();
    });
    // Handle errors
    hlsInstance.on(Hls.Events.ERROR, (_, data) => {
      debug("hls error:", data?.type, data?.details, data?.fatal);
      if (data?.fatal) {
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hlsInstance.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hlsInstance.recoverMediaError();
        }
      }
    });
    hls.value = hlsInstance;
  } else if (videoEl.canPlayType("application/vnd.apple.mpegurl")) {
    // iOS Safari (no MSE) — native HLS only
    debug("setting up native hls");
    videoEl.src = src;
    // Handle ready state
    const onReady = () => {
      videoEl.removeEventListener("loadedmetadata", onReady);
      markSourceReady();
    };
    videoEl.addEventListener("loadedmetadata", onReady);
  } else {
    debug("HLS is not supported in this browser");
  }

  // Video loads but remains paused on mount
  debug("Video initialized and paused on mount");
};

const setupIntersectionObserver = () => {
  // Clean up any existing observer
  destroyObserver();

  const videoEl = video.value;
  if (!videoEl) return;
  // Configure options based on device size
  const mediaQuery = "(min-width: 992px)";
  const options = window.matchMedia(mediaQuery).matches
    ? { rootMargin: "0px", threshold: 0.5 }
    : { rootMargin: "50px", threshold: 0.3 };

  // Create new observer
  observer.value = new IntersectionObserver((entries) => {
    const [entry] = entries;
    isInViewport.value = entry.isIntersecting;
    debug(
      `Video visibility changed: ${isInViewport.value ? "visible" : "hidden"}`,
    );

    if (!props.pause) {
      if (isInViewport.value) {
        attemptPlay();
      } else {
        debug("Video is out of viewport, pausing playback");
        pauseVideo();
      }
    }
  }, options);

  // Start observing
  observer.value.observe(videoEl);
  debug("Intersection Observer set up");
};

const destroyObserver = () => {
  if (observer.value) {
    observer.value.disconnect();
    observer.value = null;
    debug("Intersection Observer destroyed");
  }
};

const attemptPlay = () => {
  const videoEl = video.value;
  if (!videoEl || props.pause) return;

  // Don't call play() until MSE/native source is attached — otherwise Edge
  // throws: "Failed to load because no supported source was found."
  if (!isSourceReady.value) {
    pendingPlay.value = true;
    debug("play deferred until source is ready");
    return;
  }

  videoEl
    .play()
    .then(() => {
      emit("isPlaying", true);
      debug("Video playback started");
      isPlaying.value = true;
    })
    .catch((error) => {
      debug("Error playing video:", error.message);
    });
};

const playVideo = () => {
  if (isInViewport.value && !props.pause) {
    attemptPlay();
  }
};

const pauseVideo = () => {
  const videoEl = video.value;
  debug("Pausing video", videoEl);
  pendingPlay.value = false;
  if (videoEl && !videoEl.paused) {
    emit("isPlaying", false);
    videoEl.pause();
    isPlaying.value = false;
    debug("Video paused");
  }
};

const restart = () => {
  const videoEl = video.value;
  if (videoEl) {
    videoEl.currentTime = 0;
    playVideo();
  }
};

const onTimeUpdate = () => {
  const videoEl = video.value;
  if (videoEl) {
    emit("timeupdate", videoEl.currentTime, videoEl.duration);
  }
};

const onEnded = () => {
  debug("Video ended");
  emit("ended");
};

const onPlay = () => {
  // isPlaying.value = true;
  debug("Video playing");
  emit("play");
};

onMounted(async () => {
  debug("muxvideo mounted");
  await nextTick();
  initializeVideo();
  setupIntersectionObserver();
});

onBeforeUnmount(() => {
  destroyObserver();
  pendingPlay.value = false;
  if (hls.value) {
    hls.value.destroy();
    hls.value = null;
  }
});

watch(
  () => props.pause,
  (newVal) => {
    if (newVal) {
      pauseVideo();
    } else if (isInViewport.value) {
      // Only play if in viewport
      attemptPlay();
    }
  },
  { immediate: true },
);

defineExpose({
  restart,
  playVideo,
  pauseVideo,
});
</script>
<style lang="scss" scoped>
video,
img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  // filter: blur(75px);
}
img {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}
#mux-video {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
</style>
